/**
 * Group Calendar Store - Zustand
 * Manages calendar events for prep groups
 */

import { create } from 'zustand';
import { apiGet, apiPost, apiPut, apiDelete } from '@/services/api-client';
import { useAuthStore } from './auth-store';
import { logActivity } from '@/lib/activity-logger';

export interface CalendarEvent {
  id: string;
  groupId: string;
  title: string;
  description: string;
  date: string; // ISO 8601 date string
  tags: string[];
  link?: string;
  isProtected: boolean;
  authorId: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

interface GroupCalendarState {
  eventsByGroup: Record<string, CalendarEvent[]>;
  loading: boolean;

  // Actions
  fetchEvents: (groupId: string) => Promise<void>;
  createEvent: (groupId: string, data: {
    title: string;
    description: string;
    date: string;
    tags: string[];
    link?: string;
    isProtected: boolean;
  }) => Promise<CalendarEvent | null>;
  updateEvent: (groupId: string, eventId: string, data: {
    title?: string;
    description?: string;
    date?: string;
    tags?: string[];
    link?: string;
    isProtected?: boolean;
  }) => Promise<boolean>;
  deleteEvent: (groupId: string, eventId: string) => Promise<boolean>;
}

export const useGroupCalendarStore = create<GroupCalendarState>((set, _get) => ({
  eventsByGroup: {},
  loading: false,

  fetchEvents: async (groupId: string) => {
    set({ loading: true });
    try {
      const result = await apiGet<CalendarEvent[]>(`/groups/${groupId}/calendar`);
      if (result.success && result.data) {
        set(state => ({
          eventsByGroup: {
            ...state.eventsByGroup,
            [groupId]: result.data!,
          },
        }));
      }
    } catch (error) {
      console.error('Failed to fetch calendar events', error);
    } finally {
      set({ loading: false });
    }
  },

  createEvent: async (groupId: string, data) => {
    const user = useAuthStore.getState().user;
    
    // Premium check: Free users cannot create calendar events
    if (user?.subscriptionTier !== 'premium') {
      throw new Error('Endast Premium-användare kan skapa kalenderinlägg');
    }

    // Premium users: Check daily limit (50 events/day)
    const countResult = await apiGet<{ todayCount: number }>(`/groups/${groupId}/calendar/count-today`);
    if (countResult.success && countResult.data) {
      const { todayCount } = countResult.data;
      if (todayCount >= 50) {
        throw new Error('Du har nått dagens gräns på 50 kalenderinlägg');
      }
    }

    set({ loading: true });
    try {
      const result = await apiPost<CalendarEvent>(`/groups/${groupId}/calendar`, data);
      if (result.success && result.data) {
        logActivity({ action: 'ADD_EVENT', category: 'group', description: 'Calendar event created', metadata: { groupId, eventId: result.data.id, title: data.title } });
        set(state => ({
          eventsByGroup: {
            ...state.eventsByGroup,
            [groupId]: [...(state.eventsByGroup[groupId] || []), result.data!],
          },
        }));
        return result.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to create calendar event', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateEvent: async (groupId: string, eventId: string, data) => {
    const user = useAuthStore.getState().user;
    
    // Premium check: Free users cannot update calendar events
    if (user?.subscriptionTier !== 'premium') {
      throw new Error('Endast Premium-användare kan redigera kalenderinlägg');
    }

    set({ loading: true });
    try {
      const result = await apiPut<CalendarEvent>(
        `/groups/${groupId}/calendar/${eventId}/update`,
        data
      );
      if (result.success && result.data) {
        logActivity({ action: 'ADD_EVENT', category: 'group', description: 'Calendar event updated', metadata: { groupId, eventId } });
        set(state => ({
          eventsByGroup: {
            ...state.eventsByGroup,
            [groupId]: (state.eventsByGroup[groupId] || []).map(event =>
              event.id === eventId ? result.data! : event
            ),
          },
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update calendar event', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deleteEvent: async (groupId: string, eventId: string) => {
    const user = useAuthStore.getState().user;
    
    // Premium check: Free users cannot delete calendar events
    if (user?.subscriptionTier !== 'premium') {
      throw new Error('Endast Premium-användare kan radera kalenderinlägg');
    }

    set({ loading: true });
    try {
      const result = await apiDelete(
        `/groups/${groupId}/calendar/${eventId}/delete`
      );
      if (result.success) {
        logActivity({ action: 'ADD_EVENT', category: 'group', description: 'Calendar event deleted', metadata: { groupId, eventId, deleted: true } });
        set(state => ({
          eventsByGroup: {
            ...state.eventsByGroup,
            [groupId]: (state.eventsByGroup[groupId] || []).filter(
              event => event.id !== eventId
            ),
          },
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to delete calendar event', error);
      throw error;
    } finally {
      set({ loading: false });
    }
  },
}));
